import { homepageMeta } from "./_meta";
import styles from './_hcp-cah-overview.module.scss';
import HcpHero from "@/components-hcp/hero";
import HcpCahOverviewVideo from "./components/video";
import HcpCahOverviewBalance from "./components/balance";
import HcpCahOverviewCortisol from "./components/cortisol";
import HcpLongButton from "@/components-hcp/long-button";
import HcpPageFootnote from "@/components-hcp/page-footnote";
import { useMemo } from "react";
import HcpCahOverviewAddressing from "./components/addressing";
import HcpPageReferences from "@/components-hcp/page-references";
import HcpDrQuote from "@/components-hcp/dr-quote";
import Button from "@/components/button";

export default function HcpCahOverview() {
  const references = useMemo(() => [
    <>Merke DP, Auchus RJ. Congenital adrenal hyperplasia due to 21-hydroxylase deficiency. <em>N Engl J Med.</em> 2020;383(13):1248-1261. doi:10.1056/NEJMra1909786</>,
    <>Prete A, Auchus RJ, Ross RJ. Clinical advances in the pharmacotherapy of congenital adrenal hyperplasia. <em>Eur J Endocrinol.</em> 2021;186(1):R1-R14. doi:10.1530/EJE-21-0794</>,
    <>Mallappa A, Merke DP. Management challenges and therapeutic advances in congenital adrenal hyperplasia. <em>Nat Rev Endocrinol.</em> 2022;18(6):337-352. doi:10.1038/s41574-022-00655-w</>,
    <>Righi B, Ali SR, Bryce J, et al. Long-term cardiometabolic morbidity in young adults with classic 21-hydroxylase deficiency congenital adrenal hyperplasia. <em>Endocrine.</em> 2023;80(3):630-638. doi:10.1007/s12020-023-03330-w</>,
    <>Arlt W, Willis DS, Wild SH, et al. Health status of adults with congenital adrenal hyperplasia: a cohort study of 2023 patients. <em>J Clin Endocrinol Metab.</em> 2010;95(11):5110-5121. doi:10.1210/jc.2010-0917</>,
    <>Finkielstain GP, Kim MS, Sinaii N, et al. Clinical characteristics of a cohort of 244 patients with congenital adrenal hyperplasia. <em>J Clin Endocrinol Metab.</em> 2012;97(12):4429-4438. doi:10.1210/jc.2012-2102</>,
    <>Bancos I, Kim H, Cheng HK, et al. Glucocorticoid therapy in classic congenital adrenal hyperplasia: traditional and new treatment paradigms. <em>Expert Rev Endocrinol Metab</em>. 2025;20(1):33-49. doi:10.1080/17446651.2025.2450423</>,
    <>Global Strategy for Asthma Management and Prevention, 2026. Global Initiative for Asthma. Updated May 2026. Accessed June 11, 2026. <Button href="https://ginasthma.org/wp-content/uploads/2026/05/GINA-2026-Strategy-Report-WMS.pdf" leaveSiteLink targetBlank>https://ginasthma.org/wp-content/uploads/2026/05/GINA-2026-Strategy-Report-WMS.pdf</Button></>,
    <>Hindmarsh PC, Geertsma K. <em>Congenital Adrenal Hyperplasia: A Comprehensive Guide</em>. Elsevier/Academic Press; 2017.</>,
    <>Sarafoglou K, Merke DP, Reisch N, Claahsen-van der Grinten H, Falhammar H, Auchus RJ. Interpretation of steroid biomarkers in 21-hydroxylase deficiency and their use in disease management. <em>J Clin Endocrinol Metab.</em> 2023;108(9):2154-2175. doi:10.1210/clinem/dgad134</>,
    <>Koren R, Koren S, Khashper A, Benbassat C, Pekar-Zlotin M, Vaknin Z. Ovarian adrenal rest tumor in congenital adrenal hyperplasia: is medical treatment the first line option? <em>Arch Endocrinol Metab.</em> 2021;65(6):841-884. doi:10.20945/2359-3997000000415</>,
    <>Speiser PW, Arlt W, Auchus RJ, et al. Congenital adrenal hyperplasia due to steroid 21-hydroxylase deficiency: an Endocrine Society clinical practice guideline. <em>J Clin Endocrinol Metab.</em> 2018;103(11):4043-4088. doi:10.1210/jc.2018-01865</>,
    <>Data on file. Neurocrine Biosciences, Inc.</>,
    <>Schr&ouml;der MAM, Claahsen-van der Grinten HL. Novel treatments for congenital adrenal hyperplasia. <em>Rev Endocr Metab Disord.</em> 2022;23(3):631-645. doi:10.1007/s11154-022-09717-w</>,
    <>Auer MK, Nordenström A, Lajic S, Reisch N. Congenital adrenal hyperplasia. <em>Lancet.</em> 2023;401(10372):227-244. doi:10.1016/S0140-6736(22)01330-7</>,
  ], []);
  return (
      <div className={styles.container}>
        <HcpCahOverviewVideo />
        <HcpCahOverviewBalance />
        <HcpCahOverviewAddressing />
        <HcpCahOverviewCortisol />
        <HcpDrQuote
          doctor={{
            name: 'Dr. Amrit Bhangoo',
            specialty: 'Pediatric Endocrinologist',
            image: '/images/components/CAH-HCP-Specialist-Dr-Bhangoo.png',
            note: 'Compensated by Neurocrine for sharing his views.'
          }}
        >
          With the introduction of CRENESSITY we have an effective tool that suppresses ACTH. With suppression of ACTH, we can suppress the adrenal androgens… So, I think it’s a really good development in this field.”
        </HcpDrQuote>
        <div className={styles.callout_container}>
          <div className={`hcp-callout ${styles.callout}`}>
            <p>All patients with CAH need physiologic GC doses to treat their cortisol deficiency; however, controlling androgens using only GCs inherently requires supraphysiologic dosing.<sup>7</sup></p>
          </div>
        </div>
        <section>
          <HcpLongButton
            iconType="Novel-MOA"
            label={<>Novel MOA</>}
            description={<>See how CRENESSITY blocks CRF<sub>1</sub> receptor&nbsp;activation.</>}
            href="/mechanism-of-action"
            customClass={styles.long_button}
          />
          <HcpLongButton
            iconType="Clinical-Information"
            label={<>Clinical Information</>}
            description={<>Now you can lower androgen levels without increasing GCs.</>}
            href="/clinical-information#trial-1-efficacy-link"
            customClass={styles.long_button}
          />
          <HcpLongButton
            iconType="Contact-A-Rep"
            label={<>Get in Touch</>}
            description={<>Sign up to schedule a call or visit with a Neurocrine representative.</>}
            href="/support-resources#crm-sign-up"
            customClass={styles.long_button}
          />
          <HcpLongButton
            iconType="PeerConnect"
            label={<>PeerConnect</>}
            description={<span className="inline-block max-w-[500px]">Connect with colleagues who treat CAH to consult, collaborate, or transition care.</span>}
            href="/peerconnect"
            customClass={styles.long_button}
          />
        </section>
        <HcpPageFootnote> 
          ACTH=adrenocorticotropic hormone; CAH=congenital adrenal hyperplasia; CRF=corticotropin-releasing factor; <span className="text-nowrap">CRF<sub>1</sub>=corticotropin-releasing</span> factor type 1; GC=glucocorticoid; HCe=hydrocortisone equivalents; HPA=hypothalamic-pituitary-adrenal; MOA=mechanism of action; OARTs=ovarian adrenal rest tumors; TARTs=testicular adrenal rest tumors.
        </HcpPageFootnote>
        <HcpPageReferences references={references} />
      </div>
  );
}

HcpCahOverview.getLayoutProps = (_pageProps, _router) => ({
  customClass: styles.layout,
  meta: homepageMeta,
  hero: (
    <HcpHero
      backgroundImageSrc="/images/backgrounds/cah-overview-hero-bg.svg"
    >
      MANAGING <span className="text-hcp-tangerine">CAH</span> <br className="mobile--hide"/><span className="tablet:text-nowrap">IS A BALANCING ACT<sup>1,2</sup></span>
    </HcpHero>
  ),
});